import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useToastContext } from '../App';

function ModManager() {
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [library, setLibrary] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [servers, setServers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [modVersions, setModVersions] = useState(null);
  const [selectedMod, setSelectedMod] = useState(null);
  const [loader, setLoader] = useState('');
  const [gameVersion, setGameVersion] = useState('');
  const [downloading, setDownloading] = useState({});
  const addToast = useToastContext();

  useEffect(() => {
    loadLibrary();
    loadUploads();
    loadServers();
  }, []);

  async function loadLibrary() {
    try {
      const data = await api.getModLibrary();
      setLibrary(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadUploads() {
    try {
      const data = await api.getUploads();
      setUploads(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadServers() {
    try {
      const data = await api.getServers();
      setServers(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const data = await api.searchMods(searchQuery, loader, gameVersion);
      setSearchResults(data.results || []);
      if (data.results?.length === 0) {
        addToast('Keine Mods gefunden', 'info');
      }
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setSearching(false);
    }
  }

  async function handleShowVersions(mod) {
    setSelectedMod(mod);
    try {
      const versions = await api.getModVersions(mod.id, loader, gameVersion);
      setModVersions(versions);
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleDownload(version) {
    if (!version.downloadUrl) {
      addToast('Kein Download-Link verfuegbar', 'error');
      return;
    }
    setDownloading(prev => ({ ...prev, [version.id]: true }));
    try {
      await api.downloadMod(version.downloadUrl, version.fileName);
      addToast(`${version.fileName} heruntergeladen!`, 'success');
      loadLibrary();
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setDownloading(prev => ({ ...prev, [version.id]: false }));
    }
  }

  async function handleDeleteFromLibrary(fileName) {
    if (!window.confirm(`"${fileName}" aus Bibliothek loeschen?`)) return;
    try {
      await api.deleteModFromLibrary(fileName);
      addToast('Mod geloescht', 'success');
      loadLibrary();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleImportToLibrary(fileName) {
    try {
      await api.importFromUpload(fileName);
      addToast(`${fileName} in Bibliothek importiert!`, 'success');
      loadLibrary();
      loadUploads();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleImportToServer(fileName, serverId) {
    const server = servers.find(s => s.id === serverId);
    const serverName = server ? server.name : serverId;
    try {
      await api.importToServer(fileName, serverId);
      addToast(`${fileName} auf ${serverName} installiert!`, 'success');
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  return (
    <div>
      <div className="page-header">
        <h2>Mod-Manager</h2>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'search' ? 'active' : ''}`} onClick={() => setActiveTab('search')}>
          Mod suchen (Modrinth)
        </button>
        <button className={`tab ${activeTab === 'library' ? 'active' : ''}`} onClick={() => setActiveTab('library')}>
          Bibliothek ({library.length})
        </button>
        <button className={`tab ${activeTab === 'import' ? 'active' : ''}`} onClick={() => { setActiveTab('import'); loadUploads(); }}>
          Import (/minecraft/upload)
        </button>
      </div>

      {activeTab === 'search' && (
        <div>
          <form onSubmit={handleSearch} className="search-bar">
            <input
              className="form-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Mod-Name suchen..."
            />
            <select className="form-select" value={loader} onChange={e => setLoader(e.target.value)} style={{ width: 'auto' }}>
              <option value="">Alle Loader</option>
              <option value="forge">Forge</option>
              <option value="fabric">Fabric</option>
              <option value="quilt">Quilt</option>
              <option value="neoforge">NeoForge</option>
            </select>
            <input
              className="form-input"
              value={gameVersion}
              onChange={e => setGameVersion(e.target.value)}
              placeholder="MC Version"
              style={{ width: '120px' }}
            />
            <button className="btn btn-primary" type="submit" disabled={searching}>
              {searching ? <span className="spinner" /> : 'Suchen'}
            </button>
          </form>

          {searchResults.length > 0 && (
            <div className="grid grid-2">
              {searchResults.map(mod => (
                <div key={mod.id} className="card">
                  <div className="flex gap-3">
                    {mod.iconUrl && (
                      <img
                        src={mod.iconUrl}
                        alt={mod.name}
                        style={{ width: 48, height: 48, borderRadius: 8 }}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '16px' }}>{mod.name}</h3>
                      <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                        von {mod.author} | {(mod.downloads || 0).toLocaleString()} Downloads
                      </p>
                      <p className="text-sm" style={{ marginTop: 8, lineHeight: 1.4 }}>
                        {mod.description?.substring(0, 150)}
                      </p>
                      <div className="flex gap-2 mt-2">
                        {mod.categories?.slice(0, 4).map(cat => (
                          <span key={cat} className="tag">{cat}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button className="btn btn-primary btn-sm mt-4" onClick={() => handleShowVersions(mod)}>
                    Versionen anzeigen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'library' && (
        <div className="card">
          {library.length === 0 ? (
            <div className="empty-state">
              <div className="icon">&#128230;</div>
              <p>Mod-Bibliothek ist leer</p>
              <p className="text-sm text-muted">Suche und lade Mods herunter um sie hier zu sammeln</p>
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Dateiname</th>
                    <th>Groesse</th>
                    <th>Hinzugefuegt</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {library.map(mod => (
                    <tr key={mod.fileName}>
                      <td>{mod.fileName}</td>
                      <td>{formatSize(mod.size)}</td>
                      <td>{new Date(mod.addedAt).toLocaleDateString('de-DE')}</td>
                      <td>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteFromLibrary(mod.fileName)}
                        >
                          Loeschen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'import' && (
        <div className="card">
          <div style={{ marginBottom: 16 }}>
            <p className="text-sm text-muted">
              Dateien in <code>/minecraft/upload</code> ablegen und hier importieren.
              Du kannst sie in die Bibliothek oder direkt auf einen Server installieren.
            </p>
            <button className="btn btn-secondary btn-sm mt-2" onClick={loadUploads}>
              Aktualisieren
            </button>
          </div>
          {uploads.length === 0 ? (
            <div className="empty-state">
              <div className="icon">&#128193;</div>
              <p>Keine Dateien im Upload-Ordner</p>
              <p className="text-sm text-muted">Lege .jar Dateien in /minecraft/upload ab</p>
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Dateiname</th>
                    <th>Groesse</th>
                    <th>In Bibliothek</th>
                    <th>Auf Server installieren</th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.map(file => (
                    <tr key={file.fileName}>
                      <td>{file.fileName}</td>
                      <td>{formatSize(file.size)}</td>
                      <td>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleImportToLibrary(file.fileName)}
                        >
                          Importieren
                        </button>
                      </td>
                      <td>
                        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                          {servers.length === 0 ? (
                            <span className="text-sm text-muted">Keine Server vorhanden</span>
                          ) : (
                            servers.map(srv => (
                              <button
                                key={srv.id}
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleImportToServer(file.fileName, srv.id)}
                                title={`Installiere auf ${srv.name}`}
                              >
                                {srv.name}
                              </button>
                            ))
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Mod Versions Modal */}
      {modVersions && selectedMod && (
        <div className="modal-overlay" onClick={() => { setModVersions(null); setSelectedMod(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{selectedMod.name} - Versionen</h3>
            {modVersions.length === 0 ? (
              <p className="text-muted">Keine passenden Versionen gefunden</p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {modVersions.slice(0, 20).map(version => (
                  <div key={version.id} className="player-item">
                    <div>
                      <div className="fw-bold">{version.name}</div>
                      <div className="text-sm text-muted">
                        {version.gameVersions?.join(', ')} | {version.loaders?.join(', ')}
                      </div>
                      <div className="text-sm text-muted">
                        {version.fileName} ({formatSize(version.fileSize || 0)})
                      </div>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleDownload(version)}
                      disabled={downloading[version.id]}
                    >
                      {downloading[version.id] ? <span className="spinner" /> : 'Download'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => { setModVersions(null); setSelectedMod(null); }}>
                Schliessen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default ModManager;
